import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomActions } from 'src/app/dictionaries/CustomActions';
import { MattersService } from 'src/app/services/matters.service';
import { UIMessagingService } from 'src/app/services/uimessaging.service';
import { WindowsManagerService } from 'src/app/services/windows-manager.service';

import { ClioService } from 'src/app/services/clio.service';
import { FoldersService } from 'src/app/services/folders.service';
import { RedirectionService } from 'src/app/services/redirection.service';
import { AuthService } from '../../../services/auth.service';
import { LocalStorageService } from 'src/app/services/local-storage.service';

const CLIO_FOLDER_NAME = 'Clio';

// Define the type for custom action
interface ClioCustomAction {
  target_url: string;
  ui_reference: string;
  label: string;
  updated_at: string;
  created_at: string;
  id: number;
}

interface OpenClioMatterOptions {
  clioFolderId?: string;
  parentFolderId?: string;
  parentFolderName?: string;
  clioObjectId?: string;
  folderMatterInfo?: any;
  target?: string;
  documentMatterInfo?: any;
  clioDocumentId?: string;
  targetFolder?: string;
  avoidFilesCompare?: boolean;
  section?: string;
  clioDocId?: string;
  type?: number;
}

@Component({
  template: `<p></p>`,
})
export class HandleClioCustomActionsComponent implements OnInit {
  existingMatter: any;
  observable: any;
  comparisonWindowOpened: any;
  constructor(
    private windowsManager_$: WindowsManagerService,
    private activatedRoute: ActivatedRoute,
    private auth_$: AuthService,
    private matters_$: MattersService,
    private clio_$: ClioService,
    private folders_$: FoldersService,
    private router: Router,
    private uiMessaging_$: UIMessagingService,
    private redirect_$: RedirectionService,
    private localStorage_$: LocalStorageService,
  ) {}

  ngOnInit(): void {
    this.auth_$.auth.onAuthStateChanged(user => {
      if (user) this.handleExistingUser(user);
      else this.handleNotLoggedIn();
    });
  }

  handleExistingUser(user) {
    let executed = false;

    this.auth_$.userData.subscribe({
      next: async userData => {
        if (Object.keys(userData).length) {
          if (this.getUserClioCustomActions(userData)) {
            const observer = {
              next: queryParams => {
                if (executed === false) {
                  this.handleCustomActionRequest(
                    queryParams.custom_action_id,
                    queryParams.custom_action_nonce,
                    queryParams.subject_url,
                    queryParams.user_id,
                  );
                  executed = true;
                }
              },
              error: err => console.error('Observer got an error: ' + err),
              complete: () => console.log('Observer got a complete notification'),
            };
            this.activatedRoute.queryParams.subscribe(observer);
          } else {
            console.log('There are no custom actions for this user.');
          }
        }
      },
      error: err => console.log('err :', err),
      complete: () => console.log('complete'),
    });
  }

  getUserClioCustomActions(userData) {
    return userData.clioCustomActions;
  }

  handleNotLoggedIn() {
    this.activatedRoute.queryParams.subscribe(({ custom_action_id, custom_action_nonce, subject_url, user_id }) => {
      this.redirect_$.goToLogin({
        queryParams: {
          referer: this.activatedRoute.routeConfig.path,
          custom_action_id,
          custom_action_nonce,
          subject_url,
          user_id,
        },
      });
    });
  }

  handleCustomActionRequest(custom_action_id, custom_action_nonce, subject_url, user_id) {
    // Extract "/api/v4/" from the subject_url
    const origin_url = subject_url.replace('/api/v4/', '');
    this.localStorage_$.clearCCAOriginURL();
    // Save it in the local storage
    this.localStorage_$.setCCAOriginURL(origin_url);
    this.handleAction(custom_action_id, decodeURIComponent(subject_url).split('/').pop().split('%2F').pop(), user_id);
  }

  handleInvalidCustomAction() {
    this.auth_$.hideLoader();
    this.uiMessaging_$.toastMessage(
      'Invalid custom action. ' +
        'Please go back to Clio and try again. Probably you have logged in to NuageDx with a different account than the one you are logged into Clio.',
      'ERROR',
    );
    this.redirectToHome();
  }

  removeClioCustomActionUrlParams() {
    this.redirectToHome();
  }

  private async handleAction(custom_action_id, clioObjectId, user_id) {
    const custom_action = this.auth_$.userData
      .getValue()
      ['clioCustomActions'].find(ca => ca.id === parseInt(custom_action_id, 10));

    if (custom_action === undefined) {
      console.log(
        'Invalid custom action. The requested custom action does not exist in the list of the custom actions from the current logged in NuageDx user.',
      );
      this.handleInvalidCustomAction();
      return;
    }

    const target = custom_action.target_url.split('/').pop();

    // FIXME: What happen when a custom action is changed on Clio, how to update stored custom actions on user's data.
    if (['meetings', CustomActions.meetingsScheduling].includes(target)) {
      this.auth_$.preventRedirections = true;
      return await this.router.navigate(['/meetings'], { queryParams: { defaultCalendar: 'clio' } }).then(
        success => {
          console.log('Navigate to meetings page success :', success);
          this.auth_$.drawer.close();
          return;
        },
        error => console.log('error :', error),
      );
    } else {
      this.handleUIReference(custom_action, clioObjectId, user_id, target);
    }
  }

  async handleFolderAction(
    custom_action: ClioCustomAction,
    clioFolderId: string,
    user_id: string,
    options: { clioObjectId: string; target: string; clioDocId?: string; type?: number },
  ) {
    this.checkSetAvoidFilesCompare(custom_action, options);

    try {
      const matterInfo = await this.matters_$.getMatterIdFromFolderId(clioFolderId);

      this.openClioMatter(matterInfo['data']['data']['matter']['id'], user_id, {
        ...options,
        folderMatterInfo: matterInfo['data']['data'],
        clioFolderId: clioFolderId,
        targetFolder: CLIO_FOLDER_NAME,
      });
    } catch (error) {
      console.error('Error fetching matter ID from folder ID:', error);
      this.uiMessaging_$.toastMessage('Failed to fetch matter information. Please try again later.', 'ERROR');
    }
  }

  checkSetAvoidFilesCompare(custom_action, options) {
    console.log('checkSetAvoidFilesCompare');
    options.avoidFilesCompare = [CustomActions.uploadDicomDisk, CustomActions.viewMedicalImages].includes(
      custom_action.target_url.split('/').pop(),
    )
      ? true
      : false;
  }

  getClioMatterId(clioDocumentId) {
    return this.matters_$.getClioMatterIdFromDocumentId(clioDocumentId);
  }

  async handleMatterAction(custom_action, clioMatterId, user_id, options) {
    console.log('handleMatterAction');
    this.checkSetAvoidFilesCompare(custom_action, options);
    let newOptions = {};

    switch (options.target) {
      case CustomActions.goToNuageDx:
        this.redirectToHome();
        return;

      case CustomActions.openClioMatterFromDocument:
        const localClioMatterId = await this.getClioMatterId(clioMatterId);
        newOptions = { ...options, targetFolder: CLIO_FOLDER_NAME };
        this.openClioMatter(localClioMatterId, user_id, newOptions);
        return;

      case CustomActions.openClioMatterFromFolder:
        const _clioMatterId = await this.getClioMatterId(clioMatterId);
        newOptions = { ...options, targetFolder: CLIO_FOLDER_NAME };
        this.openClioMatter(_clioMatterId, user_id, newOptions);
        return;

      case CustomActions.openClioMatter:
        newOptions = { ...options, targetFolder: CLIO_FOLDER_NAME };
        break;

      case CustomActions.shareDisc:
        newOptions = { ...options, targetFolder: CLIO_FOLDER_NAME };
        break;

      case CustomActions.viewMedicalImages:
        newOptions = { ...options, section: CustomActions.viewMedicalImages };
        break;

      case CustomActions.uploadDicomDisk:
        newOptions = { ...options, section: CustomActions.uploadDicomDisk };
        break;

      default:
        newOptions = options;
        break;
    }

    this.openClioMatter(clioMatterId, user_id, newOptions);
  }

  async handleDocumentAction(custom_action, clioDocumentId, user_id, options) {
    if (CustomActions.goToNuageDx === custom_action.target_url.split('/').pop()) {
      this.redirectToHome();
      return;
    }

    this.checkSetAvoidFilesCompare(custom_action, options);
    const matterInfo = (await this.matters_$.getMatterInfoFromDocumentId(clioDocumentId))['data'];
    
    console.log('matterInfo :', matterInfo);
    if (matterInfo['error'] === true) {
      this.uiMessaging_$.toastMessage('This document has no Matter associated in Clio', 'ERROR');
      return;
    }
    this.openClioMatter(matterInfo['data']['data']['matter']['id'], user_id, {
      ...options,
      documentMatterInfo: matterInfo['data'],
      clioDocumentId: clioDocumentId,
      targetFolder: CLIO_FOLDER_NAME,
    });
  }

  async openClioMatter(matterId, user_id: string, options: OpenClioMatterOptions) {
    console.log('openClioMatter', { matterId, user_id, options });

    const existingMatter = await this.matters_$.checkClientMatterExistsFromClioMatterId(matterId);

    if (existingMatter.size > 0) {
      this.existingMatter = existingMatter.docs[0].data();

      const nuagedxCaseFolders = await this.folders_$.getFoldersByClioMatterId(matterId);
      const clioFolder = nuagedxCaseFolders.filter(f => f.name === CLIO_FOLDER_NAME)[0];
      const { folderId, name } = await this.getFolderInfoById(clioFolder.id);

      // If options.clioFolderId is not set, get it from the existing matter.
      const clioFolderId =
        options.clioFolderId || (await this.matters_$.getClioFolderIdFromCaseName(this.existingMatter.caseName));

      this.handleMatterExists(matterId, this.existingMatter, {
        ...options,
        clioFolderId: clioFolderId,
        parentFolderId: folderId,
        parentFolderName: name,
        clioDocId: options.clioDocId,
        type: options.type,
      });
    } else {
      // The Clio Matter does not exist.
      this.handleMatterDoesntExist(matterId, user_id, options);
    }
  }

  async getFolderInfoById(docId) {
    const { folderId, name } = <{ folderId: string; name: string }>await this.folders_$.getFolderById(docId);
    return { folderId, name };
  }

  async handleMatterDoesntExist(matterId, user_id, options) {
    try {
      const newMatterData = await this.matters_$.importMatterOnly(matterId, user_id);
      console.log('New matter data imported:', newMatterData);
      this.existingMatter = newMatterData;

      const nuagedxCaseFolders = await this.folders_$.getFoldersByClioMatterId(matterId);
      console.log('nuagedxCaseFolders: ', nuagedxCaseFolders);

      let clioFolder = nuagedxCaseFolders.filter(f => f.name === CLIO_FOLDER_NAME)[0];

      // Throw an error if the Clio folder does not exist.
      if (!clioFolder) {
        const errorMessage = 'Clio folder does not exist.';
        const message = `${errorMessage} Please try again later.`;

        console.error(errorMessage);
        this.uiMessaging_$.toastMessage(message, 'ERROR');

        // Create the 'Clio' folder in NuageDx.
        const createClioFolder = await this.folders_$.createClioFolder(newMatterData.caseName, user_id);
        const { folderId, name } = await this.getFolderInfoById(createClioFolder.id);

        clioFolder = { ...createClioFolder, folderId, name };
        console.log('clioFolder :>>', clioFolder);
      }

      const hme_options = {
        ...options,
        clioFolderId: newMatterData.clioFolderId,
        parentFolderId: clioFolder?.folderId,
        parentFolderName: clioFolder?.name,
        clioDocId: options.clioDocId,
        type: options.type,
      };
      this.handleMatterExists(matterId, newMatterData, hme_options);
    } catch (error) {
      console.error('Error importing matter:', error);
      this.uiMessaging_$.toastMessage('Failed to import matter. Please try again later.', 'ERROR');
    }
  }

  private async getClioAccessToken() {
    return (
      JSON.parse(this.auth_$.userData.getValue()['clioAccessToken'])['access_token'] ||
      (await this.auth_$.getClioAccessToken())
    );
  }

  async handleMatterExists(matterId: string, existingMatter, options) {
    const userdocid = this.auth_$.userData.getValue()['id'];
    const eu = this.auth_$.userData.getValue()['clioRegion'] === 'eu' ? 1 : 0;

    // SHARE_DISC
    // Check if the target is 'share_disc' and if so, validate the required parameters
    if (options.target === 'share_disc') {
      if (
        !options.parentFolderId ||
        !options.parentFolderName ||
        !options.clioObjectId ||
        !options.folderMatterInfo ||
        !options.target
      ) {
        console.error('Missing required parameters in options:', {
          parentFolderId: options.parentFolderId,
          parentFolderName: options.parentFolderName,
          clioObjectId: options.clioObjectId,
          folderMatterInfo: options.folderMatterInfo,
          target: options.target,
        });
        this.uiMessaging_$.toastMessage('Missing required parameters. Please try again.', 'ERROR');
        return;
      }

      try {
        // Validate required parameters
        if (!options.folderMatterInfo?.matter?.id || !options.folderMatterInfo?.id) {
          throw new Error('Invalid folder matter information');
        }

        await this.clio_$
          .getClioFolderContents(
            {
              folderId: options.clioObjectId,
              folderPath: `${options.folderMatterInfo.matter.id}/${options.folderMatterInfo.id}`,
              caseName: existingMatter.caseName,
              parentFolderId: options.parentFolderId,
              parentFolderName: options.parentFolderName,
              email: this.auth_$.userData.getValue()['email'] || '',
            },
            userdocid,
          )
          .catch(error => {
            throw new Error(`Error fetching Clio folder contents: ${error.message}`);
          });
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
        console.error('Error fetching Clio folder contents:', errorMessage);
        this.uiMessaging_$.toastMessage(`Failed to fetch Clio folder contents: ${errorMessage}`, 'ERROR');
      }
      return;
    }

    try {
      const clioDocumentsResponse = await this.matters_$.getDocumentsListFromClioMatterId(matterId, userdocid, eu);

      this.matters_$.getDocumentsAndFoldersListFromClioMatterId(matterId, userdocid, eu).then(res => {
        console.log('res :>>', res.data.data.data);
      });

      if (clioDocumentsResponse['data'].code === 501) {
        console.log(clioDocumentsResponse['data'].message);
        this.handleClioMatterNoDocuments(options);
      } else if (clioDocumentsResponse['data'].code === 200) {
        this.handleClioMatterDocuments(
          existingMatter,
          clioDocumentsResponse['data']['data']['data'],
          matterId,
          options,
        );
      }
    } catch (error) {
      console.error('Error fetching Clio documents:', error);
      this.uiMessaging_$.toastMessage('Failed to fetch Clio documents. Please try again later.', 'ERROR');
    }
  }

  handleClioMatterNoDocuments(options) {
    console.log('options :>', options);
    this.uiMessaging_$.toastMessage('This matter has no documents', 'INFORMATION');
    this.matters_$.openExistingMatter(this.existingMatter, this.auth_$.userData.getValue()['id'], {
      ...options,
      targetFolder: CLIO_FOLDER_NAME,
    });
  }

  async handleClioMatterDocuments(existingMatter, clioDocumentsList, matterId: string, options) {
    const existingMatterDocuments = await this.matters_$.getDocumentsListFromCaseName(
      existingMatter.caseName,
      options.clioFolderId,
    );

    const condition1 = !options.avoidFilesCompare;
    const condition2 = !this.comparisonWindowOpened;
    const condition3 =
      existingMatterDocuments.length === 0 ||
      this.checkIfPendingDocumentsToBeImported(clioDocumentsList, existingMatterDocuments) === 'yes';
    const shouldCompareDocuments = condition1 && condition2 && condition3;

    if (shouldCompareDocuments) {
      this.handleCompareDocuments(clioDocumentsList, existingMatterDocuments, matterId, options, existingMatter);
    } else {
      this.handleNoPendingDocumentsToBeImported(options);
    }
  }

  handleNoPendingDocumentsToBeImported(options) {
    console.log('options :>>', options);
    this.uiMessaging_$.toastMessage('All documents have already been imported', 'INFORMATION');
    this.matters_$.openExistingMatter(this.existingMatter, this.auth_$.userData.getValue()['id'], options);
  }

  checkIfPendingDocumentsToBeImported(clioDocumentsList, existingMatterDocuments) {
    if (existingMatterDocuments.length === 0) {
      return 'yes';
    }

    let answer = 'no';
    for (let index = 0; index < clioDocumentsList.length; index++) {
      const document = clioDocumentsList[index];
      if (!existingMatterDocuments.find(doc => doc.clioDocId === document.id)) {
        answer = 'yes';
        break;
      }
    }

    return answer;
  }

  handleCompareDocuments(clioDocumentsList, existingMatterDocuments, matterId: string, options, existingMatter) {
    if (this.windowsManager_$.clientProfileOpened) {
      console.log('clientProfileOpened');
    }

    this.matters_$
      .openFilesComparisonModal(clioDocumentsList, existingMatterDocuments, matterId, {
        ...options,
        caseName: existingMatter.caseName,
        matterData: existingMatter,
        clioFolderId: options.clioFolderId,
        clioDocId: options.clioDocId,
        type: options.type,
      })
      .afterAllClosed.subscribe(() => {
        this.comparisonWindowOpened = false;
      });

    this.comparisonWindowOpened = true;
  }

  private redirectToHome() {
    this.router.navigate(['/']);
  }

  private handleUIReference(custom_action, clioObjectId, user_id, target) {
    switch (custom_action.ui_reference) {
      case 'matters/show':
        const clioMatterId = clioObjectId;
        // this.removeClioCustomActionUrlParams();
        this.handleMatterAction(custom_action, clioMatterId, user_id, {
          target,
          clioObjectId,
          clioDocId: clioObjectId,
          type: 1,
        });
        break;
      case 'folders/show':
        const clioFolderId = clioObjectId;
        // this.removeClioCustomActionUrlParams();
        this.handleFolderAction(custom_action, clioFolderId, user_id, {
          target,
          clioObjectId,
          clioDocId: clioObjectId,
          type: 2,
        });
        break;

      case 'documents/show':
        const clioDocumentId = clioObjectId;
        // this.removeClioCustomActionUrlParams();
        this.handleDocumentAction(custom_action, clioDocumentId, user_id, {
          target,
          clioObjectId,
          clioDocId: clioObjectId,
          type: 3,
        });
        break;
      case 'contact/show':
      case 'activities/show':
        this.uiMessaging_$.toastMessage(`This action is not supported yet`, 'ERROR');
        break;
      default:
        break;
    }
  }
}
